// ============================================================================
// ROUTE PATHS - Single Source of Truth
// ============================================================================

type RouteParam = string | number;

export const ROUTES = {
  // Public/Auth Routes
  ROOT: "/",
  LOGIN: "/login",
  LOGIN_REDIRECT: "/login-redirect",
  LOGOUT_CALLBACK: "/logout/callback",
  SIGNUP: "/signup",
  FORGOT_PASSWORD: "/forgot-password",
  PASSWORD_RESET: "/password-reset",
  VALIDATE_SIGNUP_LINK: "/validate-signup-link",
  CREATE_PASSWORD: "/create-password",
  SELF_SUBSCRIBE: "/self-subscribe/:slug",
  HOME: "/home",
  NOT_FOUND: "/404",

  // Dashboard Routes
  DASHBOARD: "/dashboard",
  PROFILE: "/dashboard/profile",
  LOCATION_REPORT: "/dashboard/location-report/:locationSysId/:partId",
  DIGITAL_CAMPAIGN_REPORT: "/dashboard/digital-campaign-report/:campaignSysId",
  DIRECT_MAIL_REPORT: "/dashboard/direct-mail-report/:campaignSysId",
  LEARN_MORE: "/dashboard/learn-more/:campaignType/:campaignId",
  THANK_YOU: "/dashboard/thank-you",
  SUBSCRIPTIONS: "/dashboard/subscriptions",

  // Participant Routes
  PARTICIPANT: "/participant",
  JOIN_CAMPAIGN: "/participant/join-campaign/:campaignId",
  JOIN_POSTCARD_CAMPAIGN: "/participant/join-postcard-campaign/:campaignId",
  JOIN_NATIONAL_PLUS_UP_CAMPAIGN:
    "/participant/join-national-plus-up-campaign/:campaignId",
  CONFIRM: "/participant/confirm",
  POSTCARD_CONFIRM: "/participant/postcard-confirm",
  RRD_JOIN_CAMPAIGN_CONFIRM:
    "/participant/rrd-join-campaign-confirm/:campaignId?",
  NATIONAL_PLUS_UP_CONFIRM:
    "/participant/national-plus-up-confirm/:campaignId?",

  // Subscription Routes
  SUBSCRIPTION_PACKAGES: "/subscription-packages",
  MANAGE_SUBSCRIPTION: "/manage-subscription",
  SUBSCRIPTION_INVOICE: "/subscription-invoice",
  EDUCATION_CENTER: "/education-center/:brandId",
  PAYMENTS: "/payments",

  // Campaign Routes
  LAUNCH_CAMPAIGN: "/launch-campaign",
  LAUNCH_DIRECT_MAIL: "/launch-direct-mail",
  CAMPAIGN_OVERVIEW: "/campaign-overview",
  DIGITAL_CAMPAIGNS: "/digital-campaigns",
  DIRECT_MAIL: "/direct-mail",
  CREATE_CAMPAIGN: "/create-campaign",
  CAMPAIGN_PAUSE: "/campaign-pause/:campaignId",
  ADJUST_BUDGET: "/adjust-budget/:campaignID",
  EXTEND_CAMPAIGN: "/extend-campaign/:campaignId",

  // Email Campaign Routes
  EMAIL_CAMPAIGNS: "/email-campaigns",
  EMAIL_CAMPAIGN_JOIN: "/email-campaigns/join-email-campaign/:campaignId",
  EMAIL_CAMPAIGN_REPORT: "/email-campaign-report",
  EMAIL_CAMPAIGN_REPORT_DETAIL: "/email-campaign-report/detail/:campaignId",

  // Admin Routes
  OVERVIEW_DASHBOARD: "/overview-dashboard",
  LOCATION_MANAGEMENT: "/location-management",
  USER_MANAGEMENT: "/user-management",
  LOCATION_REPORTS: "/location-reports",
  REQUESTS: "/requests",
  MORGAN_STANLEY_ACTIVATION: "/morgan-stanley-activation/:locID?",

  // Payment Return Routes
  SUBSCRIPTION_RETURN: "/subscription-return",
  SUBSCRIPTION_CANADA_RETURN: "/subscription-canada-return",
  SUBSCRIPTION_CAD_RETURN: "/cad-subscription/return",
  SUBSCRIPTION_PAD_RETURN: "/pad-subscription/return",
  JOIN_CAMPAIGN_RETURN: "/join-campaign-return",
  RRD_JOIN_CAMPAIGN_RETURN: "/rrd-join-campaign-return",
  NATIONAL_PLUS_UP_JOIN_CAMPAIGN_RETURN:
    "/national-plus-up-join-campaign-return",
  BOOST_CAMPAIGN_RETURN: "/boost-campaign-return",
  EXTEND_CAMPAIGN_RETURN: "/extend-campaign-return",
};

// Helper functions to generate dynamic paths
export const generatePath = {
  selfSubscribe: (slug: RouteParam) =>
    ROUTES.SELF_SUBSCRIBE.replace(":slug", String(slug)),
  locationReport: (locationSysId: RouteParam, partId: RouteParam) =>
    ROUTES.LOCATION_REPORT.replace(
      ":locationSysId",
      String(locationSysId)
    ).replace(":partId", String(partId)),
  digitalCampaignReport: (campaignSysId: RouteParam) =>
    ROUTES.DIGITAL_CAMPAIGN_REPORT.replace(
      ":campaignSysId",
      String(campaignSysId)
    ),
  directMailReport: (campaignSysId: RouteParam) =>
    ROUTES.DIRECT_MAIL_REPORT.replace(":campaignSysId", String(campaignSysId)),
  learnMore: (campaignType: RouteParam, campaignId: RouteParam) =>
    ROUTES.LEARN_MORE.replace(":campaignType", String(campaignType)).replace(
      ":campaignId",
      String(campaignId)
    ),
  joinCampaign: (campaignId: RouteParam) =>
    ROUTES.JOIN_CAMPAIGN.replace(":campaignId", String(campaignId)),
  joinPostcardCampaign: (campaignId: RouteParam) =>
    ROUTES.JOIN_POSTCARD_CAMPAIGN.replace(":campaignId", String(campaignId)),
  joinNationalPlusUpCampaign: (campaignId: RouteParam) =>
    ROUTES.JOIN_NATIONAL_PLUS_UP_CAMPAIGN.replace(
      ":campaignId",
      String(campaignId)
    ),
  rrdJoinCampaignConfirm: (campaignId: RouteParam | "" = "") =>
    campaignId
      ? ROUTES.RRD_JOIN_CAMPAIGN_CONFIRM.replace(
          ":campaignId?",
          String(campaignId)
        )
      : ROUTES.RRD_JOIN_CAMPAIGN_CONFIRM.replace("/:campaignId?", ""),
  nationalPlusUpConfirm: (campaignId: RouteParam | "" = "") =>
    campaignId
      ? ROUTES.NATIONAL_PLUS_UP_CONFIRM.replace(
          ":campaignId?",
          String(campaignId)
        )
      : ROUTES.NATIONAL_PLUS_UP_CONFIRM.replace("/:campaignId?", ""),
  educationCenter: (brandId: RouteParam) =>
    ROUTES.EDUCATION_CENTER.replace(":brandId", String(brandId)),
  campaignPause: (campaignId: RouteParam) =>
    ROUTES.CAMPAIGN_PAUSE.replace(":campaignId", String(campaignId)),
  adjustBudget: (campaignID: RouteParam) =>
    ROUTES.ADJUST_BUDGET.replace(":campaignID", String(campaignID)),
  extendCampaign: (campaignId: RouteParam) =>
    ROUTES.EXTEND_CAMPAIGN.replace(":campaignId", String(campaignId)),
  emailCampaignJoin: (campaignId: RouteParam) =>
    ROUTES.EMAIL_CAMPAIGN_JOIN.replace(":campaignId", String(campaignId)),
  emailCampaignReportDetail: (campaignId: RouteParam) =>
    ROUTES.EMAIL_CAMPAIGN_REPORT_DETAIL.replace(
      ":campaignId",
      String(campaignId)
    ),
  morganStanleyActivation: (locID: RouteParam | "" = "") =>
    locID
      ? ROUTES.MORGAN_STANLEY_ACTIVATION.replace(":locID?", String(locID))
      : ROUTES.MORGAN_STANLEY_ACTIVATION.replace("/:locID?", ""),
};

// Helper to convert absolute route to relative segment for nested routes
const toRelative = (absolutePath: string, parentPath: string) => {
  return absolutePath.replace(parentPath + "/", "");
};

// Route segments for nested routes - derived from ROUTES
export const ROUTE_SEGMENTS = {
  PARTICIPANT: {
    JOIN_CAMPAIGN: toRelative(ROUTES.JOIN_CAMPAIGN, ROUTES.PARTICIPANT),
    JOIN_POSTCARD_CAMPAIGN: toRelative(
      ROUTES.JOIN_POSTCARD_CAMPAIGN,
      ROUTES.PARTICIPANT
    ),
    JOIN_NATIONAL_PLUS_UP_CAMPAIGN: toRelative(
      ROUTES.JOIN_NATIONAL_PLUS_UP_CAMPAIGN,
      ROUTES.PARTICIPANT
    ),
    CONFIRM: toRelative(ROUTES.CONFIRM, ROUTES.PARTICIPANT),
    POSTCARD_CONFIRM: toRelative(ROUTES.POSTCARD_CONFIRM, ROUTES.PARTICIPANT),
    RRD_JOIN_CAMPAIGN_CONFIRM: toRelative(
      ROUTES.RRD_JOIN_CAMPAIGN_CONFIRM,
      ROUTES.PARTICIPANT
    ),
    NATIONAL_PLUS_UP_CONFIRM: toRelative(
      ROUTES.NATIONAL_PLUS_UP_CONFIRM,
      ROUTES.PARTICIPANT
    ),
  },
  DASHBOARD: {
    PROFILE: toRelative(ROUTES.PROFILE, ROUTES.DASHBOARD),
    LOCATION_REPORT: toRelative(ROUTES.LOCATION_REPORT, ROUTES.DASHBOARD),
    DIGITAL_CAMPAIGN_REPORT: toRelative(
      ROUTES.DIGITAL_CAMPAIGN_REPORT,
      ROUTES.DASHBOARD
    ),
    DIRECT_MAIL_REPORT: toRelative(ROUTES.DIRECT_MAIL_REPORT, ROUTES.DASHBOARD),
    LEARN_MORE: toRelative(ROUTES.LEARN_MORE, ROUTES.DASHBOARD),
    THANK_YOU: toRelative(ROUTES.THANK_YOU, ROUTES.DASHBOARD),
    SUBSCRIPTIONS: toRelative(ROUTES.SUBSCRIPTIONS, ROUTES.DASHBOARD),
  },
};
